import React, { useCallback, useEffect, useState } from "react";
import { Landmark } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { ModeBadge } from "./sesStatus";
import SesSettings from "./SesSettings";
import SesFiles from "./SesFiles";
import SesLog from "./SesLog";

export default function SesHospedajes() {
  const { t } = useLang();
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const isAdmin = ["admin", "superadmin"].includes(user?.role);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/interfaces/ses/settings"); setCfg(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!cfg) return <div className="p-5 text-sm text-muted-foreground">…</div>;

  return (
    <div className="space-y-5" data-testid="ses-page">
      <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
        <div className="relative px-6 py-6 bg-[hsl(var(--sidebar))]">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/15 text-primary"><Landmark className="h-7 w-7" /></div>
              <div>
                <h1 className="font-display text-2xl font-bold tracking-tight text-foreground">{t("ses_title")}</h1>
                <p className="text-xs text-muted-foreground tracking-wide">{t("ses_subtitle")}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ModeBadge mode={cfg.mode} t={t} />
              <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${cfg.enabled ? "bg-emerald-100 text-emerald-800" : "bg-muted text-muted-foreground"}`} data-testid="ses-enabled-badge">{cfg.enabled ? t("iface_on") : t("iface_off")}</span>
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue={isAdmin ? "files" : "files"}>
        <TabsList className="h-11 rounded-xl bg-card border border-border p-1" data-testid="ses-tabs">
          <TabsTrigger value="files" className="rounded-lg px-4" data-testid="ses-tab-files">{t("ses_tab_files")}</TabsTrigger>
          {isAdmin && <TabsTrigger value="settings" className="rounded-lg px-4" data-testid="ses-tab-settings">{t("ses_tab_settings")}</TabsTrigger>}
          {isAdmin && <TabsTrigger value="log" className="rounded-lg px-4" data-testid="ses-tab-log">{t("ses_tab_log")}</TabsTrigger>}
        </TabsList>
        <TabsContent value="files" className="mt-4"><SesFiles cfg={cfg} /></TabsContent>
        {isAdmin && <TabsContent value="settings" className="mt-4"><SesSettings cfg={cfg} onSaved={setCfg} /></TabsContent>}
        {isAdmin && <TabsContent value="log" className="mt-4"><SesLog /></TabsContent>}
      </Tabs>
    </div>
  );
}
